import {Injectable} from '@angular/core';
import {OrderRepository} from '../repositories/order.repository';
import {MbDatatablesTemplate} from '../components/mb-datatable/mb-datatables-template';
import {FilterService} from "./filterer/filter.service";
import {Dtos} from "../utils/dtos";
import {Utilities} from "../utils/utilities";
import {DataManipulationService} from "./common/data-manipulation.service";
import {BaseService} from "./common/base.service";
import {ValidatorService} from "./common/validator.service";
import {PostPayloadInterface} from "../models/post-payload.interface";
import {MbColDefinition, MbColInput} from "../components/mb-datatable/models/mb-datatable.model";

@Injectable({
    providedIn: 'root'
})
export class OrderService extends BaseService<OrderRepository> {


    constructor(repo: OrderRepository, filterService: FilterService, dataManipulationService: DataManipulationService, validatorService: ValidatorService) {
        super(repo, filterService, dataManipulationService, validatorService);
        this.typeName = "Order";
    }

    reAssocBillingData(billingDataId: number, id: number, pathPostFix: string) {
        return this.repo.reAssocBillingData(billingDataId, id, pathPostFix);
    }

    public getAllByStartAndEndDate(startDate: Date, endDate: Date) {
        const filter = this.filterService.filter(Dtos.ORDER.OrderForDashboardDTO)
            .greaterThanEqual('createdAt', Utilities.dateToBackendString(startDate))
            .and()
            .lessThanEqual('createdAt', Utilities.dateToBackendString(endDate))
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public getOrdersForAnalytics(startDate: Date, endDate: Date) {
        const filter = this.filterService.filter(Dtos.ORDER.DashboardAnalyticsOrderDTO)
            .greaterThanEqual('createdAt', Utilities.dateToBackendString(startDate))
            .and()
            .lessThanEqual('createdAt', Utilities.dateToBackendString(endDate))
            .and()
            .equal('paymentStatus', 'fulfilled')
            .or()
            .equal('paymentStatus', 'notfulfilled')
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public getAllForAnalytics() {
        const startDate = new Date().addDays(-365);
        const filter = this.filterService.filter(Dtos.ORDER.DashboardAnalyticsOrderDTO)
            .greaterThanEqual('createdAt', Utilities.dateToBackendString(startDate))
            .and()
            .equal('paymentStatus', 'fulfilled')
            .or()
            .equal('paymentStatus', 'notfulfilled')
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public findById(id: any) {
        const filter = this.filterService.filter(Dtos.ORDER.OrderForDashboardDTO)
            .equal('deleted', false)
            .and()
            .equal('id', id)
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public findByPaymentId(id: any) {
        const filter = this.filterService.filter(Dtos.ORDER.OrderForDashboardDTO)
            .equal('deleted', false)
            .and()
            .equal('paymentId', id)
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public saveByType(order: any, type) {
        return this.repo.save(this.createPayload(order, type));
    }

    public saveAsStaff(order: any, type) {
        return this.repo.saveAsStaff(this.createPayload(order, type));
    }

    public saveNewDeliveryAsStaff(order: any) {
        return this.repo.saveAsStaff(this.createPayload(order, "ADMIN_DELIVERY"));
    }

    public saveNewDelivery(order: any) {
        return this.repo.save(this.createPayload(order, "ADMIN_DELIVERY"));
    }

    private createPayload(order: any, type): PostPayloadInterface {
        const now = Date.now();
        order.paymentId = 'admin' + now + 'order';
        order.type = type;
        return {
            id: null,
            type: type,
            token: now,
            attribute: order
        };
    }

    public orderSendNew(id: any) {
        return this.repo.orderSendNew(id);
    }

    public orderResend(id: any) {
        return this.repo.orderResend(id);
    }

    public orderResendAndRegenerate(id: any) {
        return this.repo.orderResendAndRegenerate(id);
    }

    public addExtraDataForOne(order: any) {
        let paymentIdregex = new RegExp('admin[0-9]*order');
        order.isAdmin = paymentIdregex.test(order.paymentId);
        order.paymentStatus = (order.paymentStatus == null ? 'Temporary' : order.paymentStatus);
        this.dataManipulationService.addNameAttributeFromFirstAndLastName(order.billingData);
        return order;
    }

    protected defineModelSpecificColumns(extraInfo?: any): any[] {
        return [
            MbColDefinition.new('billingData.name', 'order.customerName'),
            MbColDefinition.new('billingData.email', 'order.customerEmail'),
            MbColDefinition.new('orderType', 'order.orderType', MbColInput.new(MbColInput.SELECT, [
                {
                    label: 'general.SHOP',
                    value: 'SHOP'
                }, {
                    label: 'general.DELIVERY',
                    value: 'DELIVERY'
                }, {
                    label: 'enum.EVENT',
                    value: 'EVENT'
                }
            ]), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('paymentStatus', 'order.paymentStatus', MbColInput.new(MbColInput.SELECT, [
                {label: 'general.Temporary', value: 'Temporary'},
                {label: 'general.Prepared', value: 'Prepared'},
                {label: 'general.Succeeded', value: 'Succeeded'},
                {label: 'general.Canceled', value: 'Canceled'},
                {label: 'general.Expired', value: 'Expired'},
                {label: 'general.fulfilled', value: 'fulfilled'},
                {label: 'general.notfulfilled', value: 'notfulfilled'}
            ]), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('isAdmin', 'order.isAdmin', MbColInput.new(MbColInput.SELECT, [
                {label: 'general.true', value: 'true'},
                {label: 'general.false', value: 'false'}
            ]), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('orderNumber', 'order.orderNumber'),
            MbColDefinition.new('paymentId', 'order.paymentId'),
        ];
    }

}

export class CartElement {
    public personCount: number;
    public count: number;
    public productId: number;
    public isEvent: boolean;
    private _product: any;

    constructor(personCount: number, count: number, product: any, isEvent: boolean) {
        this.personCount = personCount;
        this.count = count;
        this._product = product;
        this.productId = product.id;
        this.isEvent = isEvent;
    }

    get product(): any {
        return this._product;
    }

    set product(value: any) {
        this._product = value;
        this.productId = value.id;
    }
}

export class CartElementProduct {
    id: number;
    private _product;

    constructor(id: number, product) {
        this.id = id;
        this._product = product;
    }

    get product() {
        return this._product;
    }
}

export class Cart {
    cartElements: CartElement[];
    discount: { id: null };
    totalPrice: 0;
    currency: 'huf';

    constructor() {
        this.cartElements = [];
        this.discount = {id: null};
        this.totalPrice = 0;
        this.currency = 'huf';
    }
}

export class OrderModel {
    user = {
        email: ''
    };
    type = "SHOP";
    billingData = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: ''
    };
    cart = new Cart();
    currency: 'huf';
    message = 'Ez egy adminisztrátor által létrehozott rendelés';
    paymentId = null;
    paymentStatus = 'Succeeded';
    acceptedTerms = true;
    language: '';
}

export class ContactInfoByBillingData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    taxNumber: string;
    companyName: string;

    constructor(firstName: string, lastName: string, email: string, phoneNumber: string, taxNumber?: string, companyName?: string) {
        this.firstName = firstName;
        this.lastName = lastName;
        this.email = email;
        this.phoneNumber = phoneNumber;
        this.taxNumber = taxNumber;
        this.companyName = companyName;
    }
}

export class BillingData {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    taxNumber: string;
    companyName: string;
    address: Address;

    constructor(billingData?: { firstName: string; lastName: string; phoneNumber: string; email: string }) {
        this.email = billingData?.email;
        this.firstName = billingData?.firstName;
        this.lastName = billingData?.lastName;
        this.phoneNumber = billingData?.phoneNumber;
        this.address = new Address();
    }
}

export class Address {
    id: number;
    country: string;
    city: string;
    addressType: string;
    address: string;
    houseNumber: string;
    zipCode: string;
    latitude: number;
    longitude: number;
}
